import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { useAgentAuth } from '../../../contexts/AgentAuthContext';
import { useAgentToolUsage } from '../../../hooks/useAgentToolUsage';
import { TOOL_IDS } from '../../../lib/agents/firestore-schema';
import './imo-compensation.css';

Chart.register(...registerables);

// Compensation data & logic adapted from provided HTML
const compensationPlan = {
  ranks: {
    TA: { name: 'Training Associate', commission: 0, display: 'TA' },
    Associate: { name: 'Associate', commission: 0.45, display: 'Assoc' },
    MD: { name: 'Marketing Director', commission: 0.6, display: 'MD' },
    RMD: { name: 'Regional Marketing Director', commission: 0.78, display: 'RMD' },
    PlatinumRMD: { name: 'Platinum RMD', commission: 0.88, display: 'P-RMD' },
    SFC: { name: 'Senior Field Chairman', commission: 0.88, infinityBonus: 0.01, display: 'SFC' },
    EFC: { name: 'Executive Field Chairman', commission: 0.88, infinityBonus: 0.0175, display: 'EFC' },
    SEFC: { name: 'Senior Executive Field Chairman', commission: 0.88, infinityBonus: 0.025, display: 'SEFC' },
    CEO: { name: 'CEO Marketing Director', commission: 0.88, infinityBonus: 0.03, display: 'CEO MD' },
    EVC: { name: 'Executive Vice Chairman', commission: 0.88, infinityBonus: 0.0325, display: 'EVC' },
  },
  generationalOverrides: {
    1: 0.15,
    2: 0.07,
    3: 0.0425,
    4: 0.0225,
    5: 0.0175,
    6: 0.0075,
  },
};

const rankOrder = ['TA', 'Associate', 'MD', 'RMD', 'PlatinumRMD', 'SFC', 'EFC', 'SEFC', 'CEO', 'EVC'];

function determineRank(cumulativeABV, cumulativeRecruits, months) {
  if (cumulativeABV >= 750000 && cumulativeRecruits >= 40 && months >= 6) return 'CEO';
  if (cumulativeABV >= 500000 && cumulativeRecruits >= 30 && months >= 6) return 'SEFC';
  if (cumulativeABV >= 200000 && cumulativeRecruits >= 20 && months >= 6) return 'EFC';
  if (cumulativeABV >= 100000 && cumulativeRecruits >= 10 && months >= 6) return 'SFC';
  if (cumulativeABV >= 90000 && cumulativeRecruits >= 10) return 'PlatinumRMD';
  if (cumulativeABV >= 50000 && cumulativeRecruits >= 4 && months >= 3) return 'RMD';
  if (cumulativeABV >= 25000 && cumulativeRecruits >= 3 && months >= 3) return 'MD';
  if (cumulativeRecruits >= 3 && months >= 1) return 'Associate';
  return 'TA';
}

function getNextRank(currentRank) {
  const currentIndex = rankOrder.indexOf(currentRank);
  if (currentIndex < rankOrder.length - 1 && currentIndex !== -1) {
    const next = rankOrder[currentIndex + 1];
    return {
      display: compensationPlan.ranks[next].display,
      timeline: 'With continued growth',
    };
  }
  return { display: 'Max Rank', timeline: 'Achieved!' };
}

export default function IMOCompensation() {
  const { agentProfile } = useAgentAuth();
  const usage = useAgentToolUsage(agentProfile, TOOL_IDS.IMO_COMPENSATION);
  const [scenarioType, setScenarioType] = useState('direct');
  const [currentViz, setCurrentViz] = useState('bubble');
  const [currentComplexity, setCurrentComplexity] = useState('simple');

  // simple inputs - store as strings to allow partial input
  const [monthlyABV, setMonthlyABV] = useState('10000');
  const [monthlyRecruits, setMonthlyRecruits] = useState('3');
  const [timelineMonths, setTimelineMonths] = useState('12');

  // moderate inputs
  const [modPersonalABV, setModPersonalABV] = useState('10000');
  const [modTrainingABV, setModTrainingABV] = useState('5000');
  const [modRecruits, setModRecruits] = useState('3');
  const [modRecProd, setModRecProd] = useState('3000');
  const [modTimeline, setModTimeline] = useState('12');

  // detailed inputs
  const [detPersonalABV, setDetPersonalABV] = useState('10000');
  const [detTrainingABV, setDetTrainingABV] = useState('5000');
  const [detBaseShopABV, setDetBaseShopABV] = useState('30000');
  const [detBaseShopARV, setDetBaseShopARV] = useState('10');
  const [det1stGenABV, setDet1stGenABV] = useState('50000');
  const [det2ndGenABV, setDet2ndGenABV] = useState('25000');
  const [det3rdGenABV, setDet3rdGenABV] = useState('10000');
  const [detTimeline, setDetTimeline] = useState('12');

  const [projections, setProjections] = useState([]);

  const chartRef = useRef(null);
  const canvasRef = useRef(null);

  const stats = useMemo(() => {
    if (!projections.length) {
      return {
        currentRankDisplay: 'TA',
        monthlyEarnings: 0,
        annualEarnings: 0,
        nextRankDisplay: 'MD',
        nextRankTimeline: '',
      };
    }
    const lastMonth = projections[projections.length - 1];
    const avgMonthly =
      projections.reduce((sum, m) => sum + m.totalIncome, 0) / projections.length;
    const annual = projections.reduce((sum, m) => sum + m.totalIncome, 0);
    const nextRank = getNextRank(lastMonth.rank);
    return {
      currentRankDisplay: lastMonth.rankDisplay,
      monthlyEarnings: Math.round(avgMonthly),
      annualEarnings: Math.round(annual),
      nextRankDisplay: nextRank.display,
      nextRankTimeline: nextRank.timeline,
    };
  }, [projections]);

  const calculateSimple = () => {
    const mABV = Number(monthlyABV) || 0;
    const mRecruits = Number(monthlyRecruits) || 0;
    const months = Number(timelineMonths) || 12;

    let result = [];
    let currentRank = 'Associate';
    let cumulativeABV = 0;
    let cumulativeRecruits = 0;
    let teamProduction = 0;

    for (let month = 1; month <= months; month++) {
      cumulativeABV += mABV;
      cumulativeRecruits += mRecruits;

      currentRank = determineRank(cumulativeABV, cumulativeRecruits, month);
      const rankData = compensationPlan.ranks[currentRank];

      let directIncome = 0;
      let overrides = 0;
      let bonuses = 0;

      if (scenarioType === 'direct') {
        directIncome = mABV * rankData.commission;
      } else if (scenarioType === 'team') {
        teamProduction += mRecruits * 3000;
        overrides = teamProduction * 0.15;
        directIncome = mABV * rankData.commission * 0.3;
      } else if (scenarioType === 'hybrid') {
        directIncome = mABV * rankData.commission * 0.6;
        teamProduction += mRecruits * 2500;
        overrides = teamProduction * 0.12;
      } else if (scenarioType === 'fasttrack') {
        directIncome = mABV * rankData.commission;
        if (month <= 3 && cumulativeRecruits >= 3) {
          bonuses = 2000;
        }
      }

      if (rankData.infinityBonus) {
        bonuses += (mABV + teamProduction) * rankData.infinityBonus;
      }

      const totalIncome = directIncome + overrides + bonuses;

      result.push({
        month,
        rank: currentRank,
        rankDisplay: rankData.display,
        commission: rankData.commission,
        directIncome,
        overrides,
        bonuses,
        totalIncome,
        cumulativeIncome:
          (result[month - 2]?.cumulativeIncome || 0) + totalIncome,
      });
    }

    return result;
  };

  const calculateModerate = () => {
    const personalABV = Number(modPersonalABV) || 0;
    const trainingABV = Number(modTrainingABV) || 0;
    const recruits = Number(modRecruits) || 0;
    const recruitProd = Number(modRecProd) || 0;
    const months = Number(modTimeline) || 12;

    let result = [];
    let currentRank = 'Associate';
    let cumulativeABV = 0;
    let cumulativeRecruits = 0;

    for (let month = 1; month <= months; month++) {
      const monthABV = personalABV + trainingABV;
      cumulativeABV += monthABV;
      cumulativeRecruits += recruits;

      currentRank = determineRank(cumulativeABV, cumulativeRecruits, month);
      const rankData = compensationPlan.ranks[currentRank];

      const directIncome = monthABV * rankData.commission;
      const teamProduction = cumulativeRecruits * recruitProd;
      const overrides = teamProduction * 0.1;
      const bonuses = rankData.infinityBonus
        ? teamProduction * rankData.infinityBonus
        : 0;

      const totalIncome = directIncome + overrides + bonuses;

      result.push({
        month,
        rank: currentRank,
        rankDisplay: rankData.display,
        commission: rankData.commission,
        directIncome,
        overrides,
        bonuses,
        totalIncome,
        cumulativeIncome:
          (result[month - 2]?.cumulativeIncome || 0) + totalIncome,
      });
    }

    return result;
  };

  const calculateDetailed = () => {
    const personalABV = Number(detPersonalABV) || 0;
    const trainingABV = Number(detTrainingABV) || 0;
    const baseShopABV = Number(detBaseShopABV) || 0;
    const baseShopARV = Number(detBaseShopARV) || 0;
    const gen1ABV = Number(det1stGenABV) || 0;
    const gen2ABV = Number(det2ndGenABV) || 0;
    const gen3ABV = Number(det3rdGenABV) || 0;
    const months = Number(detTimeline) || 12;

    let result = [];
    let currentRank = 'RMD';

    for (let month = 1; month <= months; month++) {
      const monthABV = personalABV + trainingABV;
      const isPlatinum = baseShopABV >= 30000 && baseShopARV >= 10;
      currentRank = isPlatinum ? 'PlatinumRMD' : 'RMD';
      const rankData = compensationPlan.ranks[currentRank];

      const directIncome = monthABV * rankData.commission;
      const gen1Override = gen1ABV * compensationPlan.generationalOverrides[1];
      const gen2Override = gen2ABV * compensationPlan.generationalOverrides[2];
      const gen3Override = gen3ABV * compensationPlan.generationalOverrides[3];
      const overrides = gen1Override + gen2Override + gen3Override;

      let bonuses = 0;
      if (isPlatinum) {
        bonuses = baseShopABV * 0.1;
      }

      const totalIncome = directIncome + overrides + bonuses;

      result.push({
        month,
        rank: currentRank,
        rankDisplay: rankData.display,
        commission: rankData.commission,
        directIncome,
        overrides,
        bonuses,
        totalIncome,
        cumulativeIncome:
          (result[month - 2]?.cumulativeIncome || 0) + totalIncome,
      });
    }

    return result;
  };

  const calculateProjection = () => {
    let data = [];
    if (currentComplexity === 'simple') {
      data = calculateSimple();
    } else if (currentComplexity === 'moderate') {
      data = calculateModerate();
    } else {
      data = calculateDetailed();
    }
    setProjections(data);
    usage.recordStep('estimate_run', {
      scenarioType,
      currentComplexity,
    });
  };

  useEffect(() => {
    // initial run
    calculateProjection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    if (!projections.length) return;

    const labels = projections.map((d) => `Month ${d.month}`);
    const totalIncomes = projections.map((d) => d.totalIncome);
    const directIncomes = projections.map((d) => d.directIncome);
    const overrides = projections.map((d) => d.overrides);
    const bonuses = projections.map((d) => d.bonuses);

    if (currentViz === 'bubble') {
      const bubbleData = projections.map((d) => ({
        x: d.month,
        y: d.totalIncome,
        r: Math.max(10, d.totalIncome / 500),
      }));

      chartRef.current = new Chart(ctx, {
        type: 'bubble',
        data: {
          datasets: [
            {
              label: 'Monthly Earnings',
              data: bubbleData,
              backgroundColor: 'rgba(102, 126, 234, 0.6)',
              borderColor: 'rgba(102, 126, 234, 1)',
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true },
          },
          scales: {
            x: {
              title: { display: true, text: 'Month' },
            },
            y: {
              title: { display: true, text: 'Income ($)' },
            },
          },
        },
      });
    } else if (currentViz === 'dashboard' || currentViz === 'comparison') {
      chartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Direct Income',
              data: directIncomes,
              backgroundColor: 'rgba(42, 157, 143, 0.8)',
              stack: 'stack',
            },
            {
              label: 'Overrides',
              data: overrides,
              backgroundColor: 'rgba(244, 162, 97, 0.8)',
              stack: 'stack',
            },
            {
              label: 'Bonuses',
              data: bonuses,
              backgroundColor: 'rgba(231, 111, 81, 0.8)',
              stack: 'stack',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true },
          },
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        },
      });
    } else if (currentViz === 'timeline') {
      chartRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Total Income',
              data: totalIncomes,
              borderColor: 'rgba(102, 126, 234, 1)',
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              fill: true,
              tension: 0.4,
              borderWidth: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true },
          },
        },
      });
    }
  }, [projections, currentViz]);

  const visibleInputs = {
    simple: currentComplexity === 'simple',
    moderate: currentComplexity === 'moderate',
    detailed: currentComplexity === 'detailed',
  };

  return (
    <div className="imo-comp-app">
      <div className="imo-header">
        <h1 className="font-semibold text-foreground">HGI Earnings Projection Tool</h1>
        <p>Visualize your income potential across all ranks &amp; scenarios.</p>
      </div>

      <div className="imo-main">
        <div className="imo-sidebar">
          <div className="imo-section-title">Income scenario</div>
          <div className="imo-control-group">
            <label>Select Income Model</label>
            <select
              value={scenarioType}
              onChange={(e) => setScenarioType(e.target.value)}
            >
              <option value="direct">Direct Sales Only (Personal Production)</option>
              <option value="team">Team Building with Recruits (Overrides)</option>
              <option value="hybrid">Hybrid (Direct Sales + Team Building)</option>
              <option value="fasttrack">Fast Track Promotions (RCTP Builder)</option>
            </select>
          </div>

          <div className="imo-section-title">Visualization style</div>
          <div className="imo-viz-selector">
            {['bubble', 'dashboard', 'comparison', 'timeline'].map((viz) => (
              <div
                key={viz}
                className={`imo-viz-option ${currentViz === viz ? 'active' : ''}`}
                onClick={() => setCurrentViz(viz)}
              >
                {viz === 'bubble' && '🫧 Bubble Chart'}
                {viz === 'dashboard' && '📊 Dashboard'}
                {viz === 'comparison' && '⚖️ Comparison'}
                {viz === 'timeline' && '📈 Timeline'}
              </div>
            ))}
          </div>

          <div className="imo-section-title">Input complexity</div>
          <div className="imo-toggle-group">
            {['simple', 'moderate', 'detailed'].map((lvl) => (
              <button
                key={lvl}
                type="button"
                className={`imo-toggle-btn ${currentComplexity === lvl ? 'active' : ''}`}
                onClick={() => setCurrentComplexity(lvl)}
              >
                {lvl[0].toUpperCase() + lvl.slice(1)}
              </button>
            ))}
          </div>

          {visibleInputs.simple && (
            <div className="imo-input-detail-section">
              <div className="imo-control-group">
                <label>Monthly Sales Volume (ABV)</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={monthlyABV}
                  onChange={(e) => setMonthlyABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setMonthlyABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-control-group">
                <label>Number of Recruits per Month</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={monthlyRecruits}
                  onChange={(e) => setMonthlyRecruits(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setMonthlyRecruits('0');
                    }
                  }}
                />
              </div>
              <div className="imo-control-group">
                <label>Projection Timeline (Months)</label>
                <input
                  type="number"
                  min="1"
                  max="36"
                  step="1"
                  value={timelineMonths}
                  onChange={(e) => setTimelineMonths(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    const num = Number(val);
                    if (val === '' || isNaN(num) || num < 1) {
                      setTimelineMonths('12');
                    } else if (num > 36) {
                      setTimelineMonths('36');
                    }
                  }}
                />
              </div>
            </div>
          )}

          {visibleInputs.moderate && (
            <div className="imo-input-detail-section">
              <div className="imo-input-row">
                <label>Personal Sales ABV</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={modPersonalABV}
                  onChange={(e) => setModPersonalABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setModPersonalABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Training Sales ABV</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={modTrainingABV}
                  onChange={(e) => setModTrainingABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setModTrainingABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Direct Recruits/Month</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={modRecruits}
                  onChange={(e) => setModRecruits(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setModRecruits('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Avg Recruit Production</label>
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={modRecProd}
                  onChange={(e) => setModRecProd(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setModRecProd('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Timeline (Months)</label>
                <input
                  type="number"
                  min="1"
                  max="36"
                  step="1"
                  value={modTimeline}
                  onChange={(e) => setModTimeline(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    const num = Number(val);
                    if (val === '' || isNaN(num) || num < 1) {
                      setModTimeline('12');
                    } else if (num > 36) {
                      setModTimeline('36');
                    }
                  }}
                />
              </div>
            </div>
          )}

          {visibleInputs.detailed && (
            <div className="imo-input-detail-section">
              <div className="imo-input-row">
                <label>Personal ABV</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={detPersonalABV}
                  onChange={(e) => setDetPersonalABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDetPersonalABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Training Sales ABV</label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={detTrainingABV}
                  onChange={(e) => setDetTrainingABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDetTrainingABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Base Shop ABV</label>
                <input
                  type="number"
                  min="0"
                  step="5000"
                  value={detBaseShopABV}
                  onChange={(e) => setDetBaseShopABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDetBaseShopABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Base Shop ARV (Recruits)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={detBaseShopARV}
                  onChange={(e) => setDetBaseShopARV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDetBaseShopARV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>1st Gen RMD ABV</label>
                <input
                  type="number"
                  min="0"
                  step="5000"
                  value={det1stGenABV}
                  onChange={(e) => setDet1stGenABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDet1stGenABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>2nd Gen RMD ABV</label>
                <input
                  type="number"
                  min="0"
                  step="5000"
                  value={det2ndGenABV}
                  onChange={(e) => setDet2ndGenABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDet2ndGenABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>3rd Gen RMD ABV</label>
                <input
                  type="number"
                  min="0"
                  step="5000"
                  value={det3rdGenABV}
                  onChange={(e) => setDet3rdGenABV(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val === '' || isNaN(Number(val))) {
                      setDet3rdGenABV('0');
                    }
                  }}
                />
              </div>
              <div className="imo-input-row">
                <label>Timeline (Months)</label>
                <input
                  type="number"
                  min="1"
                  max="36"
                  step="1"
                  value={detTimeline}
                  onChange={(e) => setDetTimeline(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    const num = Number(val);
                    if (val === '' || isNaN(num) || num < 1) {
                      setDetTimeline('12');
                    } else if (num > 36) {
                      setDetTimeline('36');
                    }
                  }}
                />
              </div>
            </div>
          )}

          <button
            type="button"
            className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-105"
            onClick={calculateProjection}
          >
            Calculate Earnings 💰
          </button>
        </div>

        <div className="imo-results">
          <div className="imo-stats-grid" id="statsGrid">
            <div className="imo-stat-card">
              <div className="imo-stat-label">Current rank</div>
              <div className="imo-stat-value">{stats.currentRankDisplay}</div>
              <div className="imo-stat-sublabel">Based on latest month</div>
            </div>
            <div className="imo-stat-card">
              <div className="imo-stat-label">Monthly earnings</div>
              <div className="imo-stat-value">
                ${stats.monthlyEarnings.toLocaleString()}
              </div>
              <div className="imo-stat-sublabel">Average per month</div>
            </div>
            <div className="imo-stat-card">
              <div className="imo-stat-label">Annual projection</div>
              <div className="imo-stat-value">
                ${stats.annualEarnings.toLocaleString()}
              </div>
              <div className="imo-stat-sublabel">Year 1 total</div>
            </div>
            <div className="imo-stat-card">
              <div className="imo-stat-label">Next promotion</div>
              <div className="imo-stat-value">{stats.nextRankDisplay}</div>
              <div className="imo-stat-sublabel">{stats.nextRankTimeline}</div>
            </div>
          </div>

          <div className="imo-chart-card" id="chartCard">
            <h3>Earnings projection over time</h3>
            <div className="imo-chart-container">
              <canvas ref={canvasRef} id="mainChart" />
            </div>
          </div>

          <div className="imo-breakdown-table">
            <h3>Monthly income breakdown by rank</h3>
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Rank</th>
                  <th>Commission %</th>
                  <th>Direct Income</th>
                  <th>Overrides</th>
                  <th>Bonuses</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {!projections.length && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center' }}>
                      Click &quot;Calculate Earnings&quot; to see your projection.
                    </td>
                  </tr>
                )}
                {projections.map((row) => (
                  <tr key={row.month}>
                    <td>Month {row.month}</td>
                    <td>
                      <span className="imo-rank-badge">{row.rankDisplay}</span>
                    </td>
                    <td>{(row.commission * 100).toFixed(0)}%</td>
                    <td className="imo-money">
                      ${Math.round(row.directIncome).toLocaleString()}
                    </td>
                    <td className="imo-money">
                      ${Math.round(row.overrides).toLocaleString()}
                    </td>
                    <td className="imo-money">
                      ${Math.round(row.bonuses).toLocaleString()}
                    </td>
                    <td className="imo-money" style={{ fontWeight: 700 }}>
                      ${Math.round(row.totalIncome).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
